import styled from 'styled-components'
import { media } from '../../../utils/mediaQuery'

export const StatsTileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: ${({ theme }) => theme.spacing.medium};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  align-items: stretch;

  ${media.tabletDown} {
    grid-template-columns: 1fr 1fr;
  }
`

export const StatsTileBox = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 14px 12px;
  text-align: center;
  /* a card is printed whole, never cut between two pages */
  @media print {
    break-inside: avoid;
  }
`

export const StatsTileValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.hero};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const StatsTileLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const StatsTileRef = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: ${({ theme }) => theme.spacing.small};
`

export const StatsVerdictBox = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.default} 14px;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};

  b {
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  }
  /* a card is printed whole, never cut between two pages */
  @media print {
    break-inside: avoid;
  }
`

export const StatsCardBox = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 14px 18px;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  /* a card is printed whole, never cut between two pages */
  @media print {
    break-inside: avoid;
  }
`

export const StatsCardHead = styled.h4`
  margin: 0 0 2px;
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.default};
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};

  small {
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    font-size: ${({ theme }) => theme.typography.fontSize.micro};
    color: ${({ theme }) => theme.colors.text.tertiary};
    text-align: right;
  }
`

export const StatsCardNote = styled.div<{ $warn?: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme, $warn }) => ($warn ? theme.colors.warning : theme.colors.text.secondary)};
  margin: 0 0 ${({ theme }) => theme.spacing.small};
`

export const StatsBarGrid = styled.div<{ $cols?: string }>`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols ?? '170px 1fr'};
  gap: ${({ theme }) => theme.spacing.default};
  align-items: center;
  padding: 3px 0;
  font-size: ${({ theme }) => theme.typography.fontSize.body};

  ${media.tabletDown} {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xsmall};
  }
`

export const StatsRowLabel = styled.span<{ $me?: boolean; $on?: boolean }>`
  color: ${({ theme, $me }) => ($me ? theme.colors.primary : theme.colors.text.primary)};
  font-weight: ${({ theme, $me, $on }) => ($me || $on ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal)};
`

export const StatsTrack = styled.div<{ $height?: number; $me?: boolean }>`
  position: relative;
  color: ${({ theme, $me }) => ($me ? theme.colors.primary : theme.colors.text.secondary)};
  display: flex;
  height: ${({ $height }) => $height ?? 22}px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background.tertiary};
`

/** Width and colour go inline: as CSS they made one class per value (hundreds per page) */
export const StatsSegment = styled.i.attrs<{
  $w: number
  $bg: string
  $dark?: boolean
}>(({ $w, $bg }) => ({ style: { width: `${$w}%`, background: $bg } }))`
  display: flex;
  align-items: center;
  justify-content: center;
  font-style: normal;
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme, $dark }) => ($dark ? theme.colors.text.inverse : theme.colors.text.primary)};
  white-space: nowrap;
  overflow: hidden;
`

export const StatsAxisTicks = styled.div`
  position: relative;
  height: 14px;
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.tertiary};

  span {
    position: absolute;
    white-space: nowrap;
  }
`

export const StatsLegendRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing.small};

  i {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    margin-right: 5px;
    vertical-align: -1px;
  }
`

export const StatsMuted = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`

/** Middle half of a distribution (25th–75th percentile) */
export const StatsBandRange = styled.i<{ $me?: boolean }>`
  position: absolute;
  top: 3px;
  height: 10px;
  border-radius: 2px;
  background: ${({ theme, $me }) => ($me ? theme.colors.primary : theme.colors.text.tertiary)};
  opacity: 0.3;
`

/** Median of a distribution */
export const StatsBandTick = styled.i<{ $me?: boolean }>`
  position: absolute;
  top: 0;
  height: 16px;
  width: 3px;
  margin-left: -1.5px;
  background: ${({ theme, $me }) => ($me ? theme.colors.primary : theme.colors.text.primary)};
`
